import { Link, useParams, useSearchParams, Navigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { Layout } from "@/components/layout/Layout";
import { Seo } from "@/components/seo/Seo";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { CTAGroup } from "@/components/CTAGroup";
import { LeadForm } from "@/components/LeadForm";
import { FAQAccordion } from "@/components/FAQAccordion";
import { Reviews } from "@/components/sections/Reviews";
import { SERVICE_IMAGES } from "@/components/sections/ServicesGrid";
import { BRAND, SERVICES, SERVICE_AREAS } from "@/data/site";
import { breadcrumbSchema, faqSchema, orgSchema, serviceSchema } from "@/lib/schema";

const ServiceDetail = () => {
  const { slug, city } = useParams();
  const [searchParams] = useSearchParams();
  const service = SERVICES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  // Backward-compat: if someone hits /services/:slug?city=jupiter,
  // redirect once to the clean path /services/:slug/jupiter.
  const legacyCity = searchParams.get("city");
  if (!city && legacyCity && SERVICE_AREAS.some((a) => a.slug === legacyCity)) {
    return <Navigate to={`/services/${slug}/${legacyCity}`} replace />;
  }

  // Dynamic location context.
  // - With /:city → hyper-local for that city.
  // - Without it → broader region ("Connecticut"), regional hub.
  const activeArea = SERVICE_AREAS.find((a) => a.slug === city);
  // If a :city segment was provided but doesn't match a real area, drop to base service URL.
  if (city && !activeArea) {
    return <Navigate to={`/services/${slug}`} replace />;
  }

  const isCitySpecific = Boolean(activeArea);
  const locationLabel = activeArea ? `${activeArea.name}, ${activeArea.state}` : "Connecticut";
  const cityName = activeArea ? activeArea.name : "Connecticut";
  const stateAbbr = activeArea ? activeArea.state : "CT";

  const url = `${BRAND.website}/services/${service.slug}${activeArea ? `/${activeArea.slug}` : ""}`;
  const title = isCitySpecific
    ? `${service.name} in ${activeArea!.name}, ${activeArea!.state}`
    : `${service.name} in Connecticut`;
  const description = isCitySpecific
    ? `${service.short} Free in-home flooring estimates from VRP Flooring LLC in ${activeArea!.name}, ${activeArea!.state} — licensed, insured, satisfaction guaranteed.`
    : `${service.short} Serving Danbury, Ridgefield, Bethel, Brookfield, Newtown and Fairfield County, CT — licensed, insured, satisfaction guaranteed.`;

  return (
    <Layout>
      <Seo
        title={title}
        description={description}
        canonical={url}
        jsonLd={[
          orgSchema(),
          serviceSchema(service.name, service.short, url, activeArea ? [`${activeArea.name}, ${activeArea.state}`] : undefined),
          faqSchema(service.faqs),
          breadcrumbSchema([
            { name: "Home", url: BRAND.website },
            { name: "Services", url: `${BRAND.website}/services` },
            { name: service.name, url },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="relative isolate overflow-hidden bg-foreground text-background">
        <div className="absolute inset-0 -z-10">
          <img src={SERVICE_IMAGES[service.slug]} alt={`${service.name} project`} className="h-full w-full object-cover opacity-45 brightness-110 contrast-105" loading="eager" />
          <div className="absolute inset-0 bg-gradient-hero" />
        </div>
        <div className="container-page py-20 md:py-28">
          <Breadcrumbs
            items={[
              { name: "Home", href: "/" },
              ...(activeArea
                ? [{ name: "Service Areas", href: "/service-areas" }, { name: activeArea.name, href: `/service-areas/${activeArea.slug}` }]
                : [{ name: "Services", href: "/services" }]),
              { name: service.name },
            ]}
            className="text-background/90"
          />
          <p className="eyebrow mt-6 mb-4 text-accent">VRP Flooring LLC</p>
          <h1 className="font-display text-4xl md:text-6xl font-bold max-w-3xl leading-[1.05] text-shadow-hero">
            {service.name} in {locationLabel}
          </h1>
          <p className="mt-5 max-w-2xl text-lg md:text-xl text-background/95 text-shadow-hero">{service.hero}</p>
          <div className="mt-8"><CTAGroup variant="onDark" /></div>
        </div>
      </section>

      {/* Flooring catalog preview */}
      {["luxury-vinyl-tile", "hardwood-flooring", "laminate-flooring", "floor-refinishing"].includes(service.slug) && (
        <section className="bg-secondary/40 py-16 md:py-20">
          <div className="container-page">
            <div className="max-w-2xl mb-6">
              <h2 className="font-display text-2xl md:text-3xl font-bold">Explore available flooring samples</h2>
              <p className="text-muted-foreground mt-2">See real examples of styles and materials available for your {service.name.toLowerCase()} project.</p>
            </div>
            <iframe
              src="https://vrpflooring-catalogo.lovable.app/catalog"
              title="Catálogo de Pisos SPC - VRP Flooring"
              width="100%"
              height="520"
              style={{ border: 0, width: "100%" }}
              loading="lazy"
            />
          </div>
        </section>
      )}

      <section className="container-page py-16 md:py-20 grid lg:grid-cols-3 gap-12">
        <div className="lg:col-span-2 space-y-12">
          {/* Quick answer for AI engines */}
          <div className="rounded-lg bg-secondary/60 border border-border p-6">
            <p className="text-sm font-semibold uppercase tracking-wider text-accentInk mb-2">Quick Answer</p>
            <p className="text-foreground/90 leading-relaxed">
              {service.short} Most {service.name.toLowerCase()} projects {isCitySpecific ? `in ${activeArea!.name}, ${activeArea!.state}` : "across Connecticut"} run <strong>{service.priceRange}</strong> and are completed on the schedule we promise — with a 100% satisfaction guarantee.
            </p>
            {!isCitySpecific && service.slug === "luxury-vinyl-plank" && (
              <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                <p>Still weighing materials? Read our <Link to="/blog/luxury-vinyl-plank-vs-hardwood-flooring" className="font-semibold text-accentInk hover:underline underline-offset-2">guide comparing LVP to hardwood</Link> for costs, durability and family-friendly performance.</p>
                <p>For a closer look at lifespan, upkeep and long-term value, see <Link to="/blog/hardwood-vs-lvp-flooring-comparison" className="font-semibold text-accentInk hover:underline underline-offset-2">how LVP compares to real hardwood</Link>.</p>
                <p>Once you choose your floor, follow our guide on <Link to="/blog/how-to-prepare-for-flooring-installation" className="font-semibold text-accentInk hover:underline underline-offset-2">how to prepare your home for LVP installation</Link>.</p>
                <p>Waterproof and scratch-resistant, LVP is <Link to="/blog/lvp-flooring-pets-connecticut" className="font-semibold text-accentInk hover:underline underline-offset-2">perfect for homes with pets</Link>.</p>
              </div>
            )}
            {!isCitySpecific && service.slug === "floor-refinishing" && (
              <p className="mt-3 text-sm text-muted-foreground">Planning ahead? <Link to="/blog/fall-hardwood-refinishing-connecticut" className="font-semibold text-accentInk hover:underline underline-offset-2">Learn why fall is the ideal season for refinishing</Link> in Connecticut.</p>
            )}
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">What's included</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {service.includes.map((it) => (
                <li key={it} className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" /><span>{it}</span></li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">Problems we solve</h2>
            <ul className="space-y-2">
              {service.problems.map((p) => (
                <li key={p} className="flex items-start gap-2"><span className="text-accent mt-1">→</span><span className="text-foreground/90">{p}</span></li>
              ))}
            </ul>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">Project types we handle</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {service.projects.map((p) => (
                <div key={p} className="rounded-md border border-border bg-surface px-4 py-3 text-sm font-medium">{p}</div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">Materials, brands, and methods</h2>
            <ul className="grid sm:grid-cols-2 gap-3">
              {service.materials.map((m) => (
                <li key={m} className="flex items-start gap-2"><Check className="h-5 w-5 text-accent mt-0.5 shrink-0" /><span>{m}</span></li>
              ))}
            </ul>
            {!isCitySpecific && service.slug === "hardwood-flooring" && (
              <div className="mt-4 space-y-2 text-muted-foreground">
                <p>Choosing between natural wood and waterproof planks? See our <Link to="/blog/luxury-vinyl-plank-vs-hardwood-flooring" className="font-semibold text-accentInk hover:underline underline-offset-2">LVP vs hardwood comparison guide</Link> before selecting your floor.</p>
                <p>Before work begins, use this <Link to="/blog/how-to-prepare-for-flooring-installation" className="font-semibold text-accentInk hover:underline underline-offset-2">preparation checklist before installation</Link> to protect your home and avoid delays.</p>
                <p>To keep your floors protected for years, read our guide on <Link to="/blog/fall-hardwood-refinishing-connecticut" className="font-semibold text-accentInk hover:underline underline-offset-2">timing your hardwood refinishing project</Link>.</p>
                <p>Have dogs or cats? Consider luxury vinyl plank as a <Link to="/blog/lvp-flooring-pets-connecticut" className="font-semibold text-accentInk hover:underline underline-offset-2">pet-friendly alternative to hardwood</Link>.</p>
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Our process</h2>
            <ol className="space-y-4">
              {service.process.map((step, i) => (
                <li key={step.title} className="flex gap-4">
                  <div className="font-display text-3xl font-bold text-accentInk w-12 shrink-0">0{i + 1}</div>
                  <div>
                    <h3 className="font-semibold text-lg">{step.title}</h3>
                    <p className="text-muted-foreground">{step.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-5">What influences the price</h2>
            <ul className="space-y-2">
              {service.priceFactors.map((f) => (
                <li key={f} className="flex items-start gap-2"><span className="text-accent mt-1">•</span><span>{f}</span></li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-muted-foreground">Typical investment in our area: <strong className="text-foreground">{service.priceRange}</strong>.</p>
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">Frequently asked questions</h2>
            <FAQAccordion items={service.faqs} idPrefix={service.slug} />
            {!isCitySpecific && service.slug === "hardwood-flooring" && (
              <p className="mt-4 text-muted-foreground">Still deciding on materials? <Link to="/blog/hardwood-vs-lvp-flooring-comparison" className="font-semibold text-accentInk hover:underline underline-offset-2">Compare hardwood to LVP flooring</Link> to weigh durability, moisture resistance, maintenance and cost.</p>
            )}
          </div>

          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">What our clients say</h2>
            <Reviews limit={2} widgetId="6a7a2f48dac6a0c9d1a9a38d" />
          </div>
        </div>

        {/* Sticky lead form */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24">
            <LeadForm defaultService={service.slug} compact />
          </div>
        </aside>
      </section>

      {/* Areas */}
      <section className="bg-secondary/40 py-16">
        <div className="container-page">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-6">
            {service.name} {activeArea ? `in ${activeArea.name} & nearby cities` : "across Connecticut"}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
            {SERVICE_AREAS.map((a) => {
              const isActive = activeArea?.slug === a.slug;
              return (
                <Link
                  key={a.slug}
                  to={`/services/${service.slug}/${a.slug}`}
                  className={`card-elev p-4 flex items-center justify-between group ${isActive ? "ring-2 ring-accent" : ""}`}
                >
                  <span className="font-medium">{a.name}</span>
                  <ArrowRight className="h-4 w-4 text-accent transition-transform group-hover:translate-x-1" />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-gradient-dark text-background py-20">
        <div className="container-page text-center max-w-2xl">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
            Ready to start your {service.name.toLowerCase()} project {isCitySpecific ? `in ${activeArea!.name}` : "in Connecticut"}?
          </h2>
          <p className="text-background/80 text-lg mb-8">Free in-home estimate with samples brought to you. No pressure. No surprises.</p>
          <CTAGroup variant="onDark" className="justify-center" />
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetail;
